import { toJS } from "mobx";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Column } from "react-table";
import { Child } from "../api/child";
import { useGlobalStore } from "../api/globalStores";
import parent from "../api/parent";
import Avatar from "../Common/Avatars/Avatar";
import IconButtonPrimary from "../Common/Buttons/IconButtonPrimary";
import ReminderCard from "../Common/Cards/ReminderCard";
import IconArrowLeftBlack from "../Common/Icons/IconArrowLeftBlack";
import IconPlus from "../Common/Icons/IconPlus";
import IconUserBlack from "../Common/Icons/IconUserBlack";
import DropDownMenu from "../Common/Menus/DropdownMenu/DropDownMenu";
import { ParentDashboardColumns } from "../Teacher/Components/Table/ParentDashboardColumns";
import Table, { IParentDashboardRow } from "../Teacher/Components/Table/Table";
import { definitions } from "../api/supabase/client";

export default observer(ParentDashboard);

//TODO: Setup proper production stripe webhook
//TODO: Move this function to a more logical place
export function getPaymentLink(child: Child) {
  if (!child.childAccount.id) {
    throw new Error("Child Account ID not defined!");
  }

  const paymentLink = new URL(process.env.REACT_APP_BUY_BRIJ_PASS_LINK!);

  paymentLink.searchParams.append(
    "client_reference_id",
    String(child.childAccount.id)
  );

  return paymentLink;
}

export async function joinFreeTrial(child: Child) {
  try {
    await child.joinFreeTrial();
    toast.success("You have successfully signed up for the free trial!");
  } catch (e: any) {
    toast.error(e.message);
  }
}

function proceedToPayment(child: Child) {
  const paymentLink = getPaymentLink(child);
  window.location.href = paymentLink.href;
}

function dateToDDMMYYYY(date: Date | undefined | string) {
  if (!date) return "N/A";
  if (typeof date === "string") {
    date = new Date(date);
  }
  const yyyy = date.getFullYear();
  let mm = date.getMonth() + 1; // Months start at 0!
  let dd = date.getDate();

  const formattedToday = dd + "/" + mm + "/" + yyyy;

  return formattedToday;
}

function ParentDashboard() {
  const globalStores = useGlobalStore();
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[]>([]);
  const [dashboardData, setDashboardData] = useState<IParentDashboardRow[]>([]);
  const [showDropDown, setShowDropDown] = useState(false);
  const [needOnboardingChildren, setNeedOnboardingChildren] = useState<Child[]>(
    []
  );
  const [paymentDetails, setPaymentDetails] = useState<
    definitions["PaymentStatus"][]
  >([]);

  useEffect(() => {
    const run = async () => {
      const newNeedOnboardingChilds = await Promise.all(
        children.map(async (child) => {
          const needOnboarding = await child.continueOnboardingQuiz();
          if (needOnboarding) return child;
          else return undefined;
        })
      );
      setNeedOnboardingChildren(
        newNeedOnboardingChilds.filter(
          (child) => child !== undefined
        ) as Child[]
      );
    };

    run();
  }, [children]);

  useEffect(() => {
    const run = async () => {
      const newPaymentDetails =
        await globalStores.parentStore.getPaymentDetails();
      setPaymentDetails(newPaymentDetails);
    };

    run();
  }, [children]);

  useEffect(() => {
    const parentDashboardData: IParentDashboardRow[] = children.flatMap(
      //@ts-ignore
      (child) => {
        if (child.classes.length === 0) {
          return [
            {
              avatar: <Avatar icon={Child.getIcon(child)} active={false} />,
              name: child.userDetail.firstName ?? "",
              course_name: "N/A",
              course_start: "N/A",
              course_end: "N/A",
              level: "N/A",
              onboarding_quiz: needOnboardingChildren.some(
                (needOnboardingChild) => {
                  return (
                    needOnboardingChild.childAccount.id ===
                    child.childAccount.id
                  );
                }
              )
                ? "In Progress"
                : "Completed",
              child,
            },
          ];
        }

        return child.classes.map((childClass) => {
          return {
            avatar: <Avatar icon={Child.getIcon(child)} active={false} />,
            name: child.userDetail.firstName ?? "",
            course_name: childClass.name ?? String(childClass.id) ?? "",
            course_start: dateToDDMMYYYY(childClass.startDate),
            course_end: dateToDDMMYYYY(childClass.endDate),
            level: childClass.level,
            onboarding_quiz: needOnboardingChildren.some(
              (needOnboardingChild) =>
                needOnboardingChild.childAccount.id === child.childAccount.id
            )
              ? "In Progress"
              : "Completed",
            child,
          };
        });
      }
    );
    setDashboardData(parentDashboardData);
  }, [children, needOnboardingChildren]);

  useEffect(() => {
    const run = async () => {
      if (!globalStores?.parentStore?.parentAccount?.id) {
        return;
      }
      const children = await globalStores.parentStore.getChildren();
      setChildren(children);
    };

    run();
  }, [globalStores?.parentStore?.parentAccount?.id]);

  const payingChildIDs = paymentDetails.map(
    (paymentDetail) => paymentDetail.account
  );
  const nonPayingChildren = children.filter((child) => {
    return !payingChildIDs.includes(child.childAccount.id ?? -1);
  });

  return (
    <>
      <div className="bg-brij-Default h-full w-full flex flex-col relative lg:bg-hill_children_white bg-contain bg-no-repeat bg-bottom backdrop-opacity-50 overflow-y-scroll">
        {/* HEADER */}
        <div className="flex gap-6 p-8 pb-3 items-center w-full z-20">
          <div
            className="w-16 h-16 cursor-pointer"
            onClick={() => {
              setShowDropDown(!showDropDown);
            }}
          >
            <Avatar icon={"bear"} active={false} large />
            <div className={showDropDown ? "absolute left-11" : "hidden"}>
              <DropDownMenu
                menuItems={[
                  {
                    text: "My Children",
                    icon: <IconUserBlack />,
                    onClick: () => {
                      navigate("/parent/child_selection");
                    },
                  },
                  {
                    text: "Sign Out",
                    icon: <IconArrowLeftBlack />,
                    onClick: () => {
                      toast.promise(
                        (async () => {
                          await parent.signOut();
                          navigate("/parent/login");
                        })(),
                        {
                          success: "Sign out Success!",
                          error: (e) => e.message,
                          loading: "Signing out...",
                        }
                      );
                    },
                  },
                ]}
              />
            </div>
          </div>
          <p className="font-medium text-xlarge">
            Welcome back, {globalStores.parentStore.userDetail.firstName}
          </p>
        </div>
        {/* DASHBOARD */}
        <div className="w-full flex flex-col md:flex-row items-center justify-center gap-8 pb-12">
          {/* <div className="flex flex-col h-full gap-2 w-full md:w-[290px] px-6 md:px-0">
            <p className="text-heading3 font-semibold">Notifications</p>
            {nonPayingChildren.map((child) => {
              if (!child.usedFreeTrial) {
                // return (
                //   <ReminderCard
                //     header={"IMPORTANT!"}
                //     content={`${child.userDetail.firstName} is eligible to join our free holiday camp!`}
                //     buttonText={"JOIN NOW"}
                //     onClick={() => joinFreeTrial(child)}
                //   />
                // );
              } else {
                return (
                  <ReminderCard
                    header={"IMPORTANT!"}
                    content={`For ${child.userDetail.firstName} to attend classes, please first complete payment`}
                    buttonText={"PROCEED TO PAYMENT"}
                    onClick={() => proceedToPayment(child)}
                  />
                );
              }
            })}
            {needOnboardingChildren.map((child) => {
              return (
                <ReminderCard
                  header={"NOTIFICATION"}
                  content={`${child.userDetail.firstName} has not finished his onboarding quiz`}
                />
              );
            })}
          </div> */}
          <div className="flex flex-col h-full gap-2 w-full md:w-[500px] px-6 md:px-0">
            <div className="flex w-full justify-between">
              <p className="text-heading3 font-semibold">My Children</p>
              <IconButtonPrimary
                onClick={() => navigate("/parent/flow/child_details")}
                icon={
                  <div className="w-5 h-5 stroke-white">
                    <IconPlus />
                  </div>
                }
                text={"Add Child"}
                color={"peach"}
              />
            </div>
            <div className="overflow-x-scroll">
              <Table
                rowData={dashboardData}
                colData={ParentDashboardColumns as any}
                onClickRow={async (row: IParentDashboardRow) => {
                  if (!row.child.childAccount.id)
                    throw new Error(
                      "Child ID not defined for Parent Dashboard Row"
                    );
                  await globalStores.childStore.init(row.child.childAccount.id);
                  navigate("/student/login");
                }}
              />
            </div>
          </div>
        </div>
        <div className="w-full flex justify-center pb-4">
          {/* TODO: Make this visible throughout the page */}
        </div>
      </div>
      <FAQButton />
    </>
  );
}

export function FAQButton() {
  const redirectLink = process.env.REACT_APP_NOTION_FAQ_LINK;

  return (
    <a
      className="flex justify-center cursor-pointer fixed inset-x-0 bottom-5 w-full px-4"
      href={redirectLink}
      target="_blank"
      rel="noreferrer"
    >
      <div className="flex items-start justify-start gap-2.5 overflow-clip rounded-xl bg-white p-[18px] drop-shadow-lg">
        <div className="relative text-center">
          <p className="inline font-['Poppins'] text-lg font-medium leading-[1.35] text-[rgba(64,52,52,1)]">
            Need Help? Have a Question?
          </p>
          <p className="inline font-['Poppins'] text-lg font-medium leading-[1.35] text-[rgba(50,50,50,1)]">
            {" "}
          </p>
          <p className="inline font-['Poppins'] text-lg font-medium leading-[1.35] text-[rgba(102,0,18,1)] underline">
            Click Here
          </p>
        </div>
      </div>
    </a>
  );
}
